import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Settings, DollarSign, Building, Save, Users, Trash2, Plus, Sliders
} from 'lucide-react';
import { transactionAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { useToast } from '../contexts/ToastContext';
import { useCemeterySettings } from '../contexts/CemeterySettingsContext';
import { formatCurrency } from '../utils/formatters';
import NumberInput from '../components/NumberInput';
import FieldCustomization from '../components/FieldCustomization';

const AdminSettings = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const [activeTab, setActiveTab] = useState('cemetery-fees');
  
  // 묘지 설정 Context 사용
  const {
    cemeteries,
    isLoadingCemeteries,
    isLoadingSettings,
    updateLocalSettings,
    saveCemeterySettings,
    getCemeterySettings,
    updateCemeterySettingsMutation,
    refetchCemeteries // 🔧 refetch 함수 추가
  } = useCemeterySettings();

  // 🔧 묘지 정보가 없을 때 자동으로 다시 로드 - 로그인된 사용자일 때만 실행
  useEffect(() => {
    if (user && !isLoadingCemeteries && (!cemeteries || cemeteries.length === 0)) {
      console.log('AdminSettings: 묘지 정보가 없음 - 자동으로 다시 로드 시도');
      setTimeout(() => {
        refetchCemeteries();
      }, 1000);
    }
  }, [user, cemeteries, isLoadingCemeteries, refetchCemeteries]);
  
  // 로컬 상태 관리
  const [localCosts, setLocalCosts] = useState({});
  const [showAddStoneCostForm, setShowAddStoneCostForm] = useState(null);
  const [editingStoneCost, setEditingStoneCost] = useState(null);
  const [newStoneCost, setNewStoneCost] = useState({
    stone_type: '',
    cost: 0,
    description: ''
  });



  // 석물 비용 조회 - 로그인된 사용자일 때만 실행
  const { data: stoneCosts, isLoading: isLoadingStoneCosts } = useQuery({
    queryKey: ['stone-costs'],
    queryFn: () => Promise.all(
      cemeteries?.map(cemetery => 
        transactionAPI.getStoneCosts(cemetery.id).catch(() => null)
      ) || []
    ),
    select: (responses) => {
      const costs = {};
      responses.forEach((response, index) => {
        if (response && response.data && cemeteries?.[index]) {
          // API 응답 구조: { success: true, data: stoneCosts }
          costs[cemeteries[index].id] = response.data.data || [];
          console.log(`석물 비용 조회 - 묘지 ${cemeteries[index].id}:`, response.data.data);
        }
      });
      console.log('전체 석물 비용 데이터:', costs);
      return costs;
    },
    enabled: !!user && !!cemeteries && cemeteries.length > 0, // 🔧 로그인된 사용자이고 묘지가 있을 때만 실행
    staleTime: 5 * 60 * 1000, // 5분간 데이터 신선도 유지
    cacheTime: 10 * 60 * 1000, // 10분간 캐시 유지
    refetchOnWindowFocus: false, // 윈도우 포커스 시 재요청 방지
    refetchOnMount: true // 컴포넌트 마운트 시 재요청
  });



  // 석물 비용 추가 뮤테이션
  const addStoneCostMutation = useMutation({
    mutationFn: ({ cemeteryId, data }) => transactionAPI.createStoneCost(cemeteryId, data),
    onSuccess: (response, variables) => {
      console.log('석물 비용 추가 성공:', response);
      queryClient.invalidateQueries(['stone-costs']);
      success('석물 비용이 추가되었습니다.');
      setShowAddStoneCostForm(null);
      setNewStoneCost({ stone_type: '', cost: 0, description: '' });
    },
    onError: (error) => {
      showError('석물 비용 추가에 실패했습니다.');
      console.error('석물 비용 추가 실패:', error);
    }
  });

  // 석물 비용 수정 뮤테이션
  const updateStoneCostMutation = useMutation({
    mutationFn: ({ cemeteryId, costId, data }) => transactionAPI.updateStoneCost(cemeteryId, costId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['stone-costs']);
      success('석물 비용이 수정되었습니다.');
      setEditingStoneCost(null);
    },
    onError: (error) => {
      showError('석물 비용 수정에 실패했습니다.');
      console.error('석물 비용 수정 실패:', error);
    }
  });

  // 석물 비용 삭제 뮤테이션
  const deleteStoneCostMutation = useMutation({
    mutationFn: ({ cemeteryId, costId }) => transactionAPI.deleteStoneCost(cemeteryId, costId),
    onSuccess: () => {
      queryClient.invalidateQueries(['stone-costs']);
      success('석물 비용이 삭제되었습니다.');
    },
    onError: (error) => {
      showError('석물 비용 삭제에 실패했습니다.');
      console.error('석물 비용 삭제 실패:', error);
    }
  });

  // 권한 확인
  if (!hasPermission(user?.role, 'ADMIN_PAGE_ACCESS')) {
    return (
      <div className="flex-center min-h-screen">
        <div className="text-center">
          <Settings size={64} className="mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">접근 권한이 없습니다</h2>
          <p className="text-gray-500">관리자 권한이 필요합니다.</p>
        </div>
      </div>
    );
  }



  // 로컬 비용 업데이트 함수
  const updateLocalCosts = (cemeteryId, field, value) => {
    setLocalCosts(prev => ({
      ...prev,
      [cemeteryId]: {
        ...prev[cemeteryId],
        [field]: value
      }
    }));
  };

  const handleCemeterySettingsSave = (cemeteryId, settings) => {
    saveCemeterySettings(cemeteryId, settings);
  };

  const handleAddStoneCost = (cemeteryId) => {
    if (!newStoneCost.stone_type || newStoneCost.cost <= 0) {
      showError('석물 유형과 비용을 입력해주세요.');
      return;
    }
    addStoneCostMutation.mutate({ cemeteryId, data: newStoneCost });
  };

  const handleEditStoneCost = (cemeteryId, cost) => {
    setEditingStoneCost({ ...cost, cemeteryId });
  };

  const handleUpdateStoneCost = (cemeteryId, costId, data) => {
    if (!data.stone_type || data.cost <= 0) {
      showError('석물 유형과 비용을 입력해주세요.');
      return;
    }
    updateStoneCostMutation.mutate({ cemeteryId, costId, data });
  };

  const handleDeleteStoneCost = (cemeteryId, costId) => {
    if (window.confirm('정말로 이 석물 비용을 삭제하시겠습니까?')) {
      deleteStoneCostMutation.mutate({ cemeteryId, costId });
    }
  };

  const handleCancelEdit = () => {
    setEditingStoneCost(null);
    setShowAddStoneCostForm(null);
    setNewStoneCost({ stone_type: '', cost: 0, description: '' });
  };

  if (isLoadingCemeteries || isLoadingSettings || isLoadingStoneCosts) {
    return (
      <div className="loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
        설정 정보를 불러오는 중...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">관리자 설정</h1>
          <p className="text-gray-600 mt-1">묘지별 요금 및 석물 비용을 관리하세요</p>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { id: 'cemetery-fees', name: '묘지별 요금', icon: DollarSign },
            { id: 'stone-costs', name: '석물 비용', icon: Building },
            { id: 'field-customization', name: '필드 커스터마이징', icon: Sliders },
            ...(user?.role === 'admin' ? [{ id: 'system-settings', name: '시스템 설정', icon: Settings }] : [])
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {/* <Icon size={16} /> */}
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* 탭 콘텐츠 */}
      <div className="space-y-6">
        {/* 묘지별 요금 탭 */}
        {activeTab === 'cemetery-fees' && (
          <div className="space-y-6">
            {Array.isArray(cemeteries) && cemeteries.length > 0 ? (
              cemeteries.map((cemetery) => {
                const currentLocalSettings = getCemeterySettings(cemetery.id);
                
                return (
                  <div key={cemetery.id} className="card">
                    <div className="card-header">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <h3 className="text-lg font-semibold">{cemetery.name}</h3>
                        <button
                          onClick={() => handleCemeterySettingsSave(cemetery.id, currentLocalSettings)}
                          disabled={updateCemeterySettingsMutation.isLoading}
                          className="btn btn-primary"
                        >
                          <Save size={16} className="mr-2" />
                          {updateCemeterySettingsMutation.isLoading ? '저장 중...' : '저장'}
                        </button>
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="form-label">사용료 (평당)</label>
                          <NumberInput
                            value={currentLocalSettings.usage_fee_per_sqm}
                            onChange={(val) => updateLocalSettings(cemetery.id, 'usage_fee_per_sqm', val)}
                            className="input"
                            placeholder="사용료를 입력하세요"
                          />
                          <p className="text-sm text-gray-500 mt-1">
                            묘소 크기에 따라 자동 계산됩니다.
                          </p>
                        </div>
                        <div>
                          <label className="form-label">관리비 (1년 단위 평당)</label>
                          <NumberInput
                            value={currentLocalSettings.management_fee_per_sqm}
                            onChange={(val) => updateLocalSettings(cemetery.id, 'management_fee_per_sqm', val)}
                            className="input"
                            placeholder="관리비를 입력하세요"
                          />
                          <p className="text-sm text-gray-500 mt-1">
                            5년 단위로 자동 발생됩니다.
                          </p>
                        </div>
                        <div>
                          <label className="form-label">관리비 발생 주기</label>
                          <select 
                            className="select"
                            value={currentLocalSettings.management_fee_cycle || 5}
                            onChange={(e) => updateLocalSettings(cemetery.id, 'management_fee_cycle', e.target.value)}
                          >
                            <option value="1">1년</option>
                            <option value="3">3년</option>
                            <option value="5">5년</option>
                            <option value="10">10년</option>
                          </select>
                          <p className="text-sm text-gray-500 mt-1">
                            계약 기간 이후 관리비 발생 주기를 설정합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">등록된 묘지가 없습니다.</p>
              </div>
            )}
          </div>
        )}

        {/* 석물 비용 탭 */}
        {activeTab === 'stone-costs' && (
          <div className="space-y-6">
            {Array.isArray(cemeteries) && cemeteries.length > 0 ? (
              cemeteries.map((cemetery) => {
                const costs = stoneCosts?.[cemetery.id] || [];
                console.log(`묘지 ${cemetery.id} (${cemetery.name})의 석물 비용:`, costs);
                return (
                  <div key={cemetery.id} className="card">
                    <div className="card-header">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <h3 className="text-lg font-semibold">{cemetery.name} - 석물 비용</h3>
                        <button
                          onClick={() => setShowAddStoneCostForm(cemetery.id)}
                          className="btn btn-primary"
                        >
                          <Plus size={16} className="mr-2" />
                          석물 비용 추가
                        </button>
                      </div>
                    </div>
                    <div className="card-body">
                      {/* 석물 비용 추가 폼 */}
                      {showAddStoneCostForm === cemetery.id && (
                        <div className="mb-6 p-4 border border-blue-200 rounded-lg bg-blue-50">
                          <h4 className="font-medium text-gray-900 mb-4">새 석물 비용 추가</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="form-label">석물 유형</label>
                              <input
                                type="text"
                                value={newStoneCost.stone_type}
                                onChange={(e) => setNewStoneCost({ ...newStoneCost, stone_type: e.target.value })}
                                className="input"
                                placeholder="예: 묘테, 상석, 비석"
                              />
                            </div>
                            <div>
                              <label className="form-label">비용</label>
                              <NumberInput
                                value={newStoneCost.cost}
                                onChange={(val) => setNewStoneCost({ ...newStoneCost, cost: val })}
                                className="input"
                                placeholder="비용"
                              />
                            </div>
                            <div>
                              <label className="form-label">설명</label>
                              <input
                                type="text"
                                value={newStoneCost.description}
                                onChange={(e) => setNewStoneCost({ ...newStoneCost, description: e.target.value })}
                                className="input"
                                placeholder="설명 (선택사항)"
                              />
                            </div>
                          </div>
                          <div className="flex space-x-2 mt-4">
                            <button
                              onClick={() => handleAddStoneCost(cemetery.id)}
                              disabled={addStoneCostMutation.isLoading}
                              className="btn btn-primary"
                            >
                              {addStoneCostMutation.isLoading ? '추가 중...' : '추가'}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="btn btn-outline"
                            >
                              취소
                            </button>
                          </div>
                        </div>
                      )}

                      {/* 석물 비용 목록 */}
                      {costs.length > 0 ? (
                        <div className="space-y-4">
                          {costs.map((cost) => (
                            <div key={cost.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                              <div className="flex-1">
                                <h4 className="font-medium text-gray-900">{cost.stone_type}</h4>
                                {cost.description && (
                                  <p className="text-sm text-gray-500 mt-1">{cost.description}</p>
                                )}
                              </div>
                              <div className="flex items-center space-x-4">
                                <span className="text-lg font-bold text-blue-600">
                                  {formatCurrency(cost.cost)}원
                                </span>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleEditStoneCost(cemetery.id, cost)}
                                    className="btn btn-outline btn-sm"
                                  >
                                    수정
                                  </button>
                                  <button
                                    onClick={() => handleDeleteStoneCost(cemetery.id, cost.id)}
                                    className="btn btn-danger btn-sm"
                                  >
                                    삭제
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <p>등록된 석물 비용이 없습니다.</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">등록된 묘지가 없습니다.</p>
              </div>
            )}
          </div>
        )}

        {/* 필드 커스터마이징 탭 */}
        {activeTab === 'field-customization' && (
          <div className="space-y-6">
            {Array.isArray(cemeteries) && cemeteries.length > 0 ? (
              cemeteries.map((cemetery) => (
                <FieldCustomization
                  key={cemetery.id}
                  cemeteryId={cemetery.id}
                  cemeteryName={cemetery.name}
                />
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">등록된 묘지가 없습니다.</p>
              </div>
            )}
          </div>
        )}

        {/* 시스템 설정 탭 */}
        {activeTab === 'system-settings' && (
          <div className="space-y-6">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">시스템 설정</h3>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">자동 알림 설정</label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input type="checkbox" className="mr-2" defaultChecked />
                        관리비 납부 알림
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" className="mr-2" defaultChecked />
                        계약 만료 알림
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" className="mr-2" />
                        보수 필요 알림
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="form-label">데이터 백업 주기</label>
                    <select className="select" defaultValue="daily">
                      <option value="daily">매일</option>
                      <option value="weekly">매주</option>
                      <option value="monthly">매월</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">로그 보관 기간</label>
                    <select className="select" defaultValue="1">
                      <option value="1">1년</option>
                      <option value="3">3년</option>
                      <option value="5">5년</option>
                      <option value="10">10년</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6">
                  <button className="btn btn-primary">
                    <Save size={16} className="mr-2" />
                    시스템 설정 저장
                  </button>
                </div>
              </div>
            </div>

            {/* 시스템 정보 */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">시스템 정보</h3>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">시스템 버전</label>
                    <p className="text-gray-900">V6.0.0</p>
                  </div>
                  <div>
                    <label className="form-label">최종 업데이트</label>
                    <p className="text-gray-900">2025년 1월</p>
                  </div>
                  <div>
                    <label className="form-label">총 묘지 수</label>
                    <p className="text-gray-900">{Array.isArray(cemeteries) ? cemeteries.length : 0}개</p>
                  </div>
                  <div>
                    <label className="form-label">총 계약 수</label>
                    <p className="text-gray-900">-</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 석물 비용 수정 모달 */}
      {editingStoneCost && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={handleCancelEdit}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">석물 비용 수정</h3>
            </div>
            <div className="px-6 py-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">석물 유형</label>
                  <input
                    type="text"
                    value={editingStoneCost.stone_type}
                    onChange={(e) => setEditingStoneCost({ ...editingStoneCost, stone_type: e.target.value })}
                    className="input"
                    placeholder="예: 묘테, 상석, 비석"
                  />
                </div>
                <div>
                  <label className="form-label">비용</label>
                  <NumberInput
                    value={editingStoneCost.cost}
                    onChange={(val) => setEditingStoneCost({ ...editingStoneCost, cost: val })}
                    className="input"
                    placeholder="비용"
                  />
                </div>
                <div>
                  <label className="form-label">설명</label>
                  <input
                    type="text"
                    value={editingStoneCost.description || ''}
                    onChange={(e) => setEditingStoneCost({ ...editingStoneCost, description: e.target.value })}
                    className="input"
                    placeholder="설명 (선택사항)"
                  />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
              <button
                onClick={handleCancelEdit}
                className="btn btn-outline"
              >
                취소
              </button>
              <button
                onClick={() => handleUpdateStoneCost(editingStoneCost.cemeteryId, editingStoneCost.id, editingStoneCost)}
                disabled={updateStoneCostMutation.isLoading}
                className="btn btn-primary"
              >
                {updateStoneCostMutation.isLoading ? '수정 중...' : '수정'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings; 